import { makeStyles } from "@material-ui/core";
import React, { useContext, useEffect, useMemo } from "react";

import { DEVICE_MANAGEMENT_LOGIN_HOST_PATH } from "../../../constants";
import { HelpVideo } from "../../../enums/helpVideo";
import { useAuth } from "../../../features/auth/context";
import { useForceRerender } from "../../../hooks/useForceRerender";
import { useFullSitesLoader } from "../../../hooks/useFullSitesLoader";
import { useOrgSiteContext } from "../../../hooks/useOrgSiteContext";
import SignalRContext from "../../../signalr/context";
import { hubConnection } from "../../../signalr/provider";
import { navLinkUrl } from "../../../utils";
import { PageHeader } from "../../PageHeader";
import { OrgSelect } from "../../shared/OrgSelect";

import { OpenCommissioningSiteWidget } from "./OpenCommissioningSiteWidget";
import { OpenSiteConfigurationWidget } from "./OpenSiteConfigurationWidget";
import { ServerStatus } from "./ServerStatus";
import { SiteInfo } from "./SiteInfo";
import { SiteIssuesSummary, SiteIssuesTable, useSiteIssues } from "./SiteIssues";
import { SitesStats } from "./SitesStats";
import { ReportsLink } from "./ReportsLink";
import { SitesInMaintenanceModeWidget } from "./SitesInMaintenanceModeWidget";
import { useSiteCommissioningSummary } from "./useSiteCommissioningSummary";

const useStyles = makeStyles(() => ({
    container: {
        display: "flex",
        flexDirection: "column",
        gap: "20px",
    },
    widgetsRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: "20px",
        alignItems: "flex-start",
    },
}));

export function HomePage() {
    const {
        isGlobalAdmin,
        isDealerAdmin,
        isDealerEmployee,
        isOrgAdmin,
        isOrgEmployee,
        isSiteAdmin,
        isSiteEmployee,
    } = useAuth();

    const deassocciateSiteFromContext = isOrgAdmin || isOrgEmployee || isSiteAdmin || isSiteEmployee;
    const showCommissioningSiteLink = isGlobalAdmin || isDealerAdmin || isDealerEmployee;
    // Matches the "Configure" row action's visibility on the sites list (SiteRowActions.tsx /
    // table.tsx): every admin tier, no employee tier.
    const showSiteConfigLink = !isDealerEmployee && !isOrgEmployee && !isSiteEmployee;
    const showHardwareSummary = !isOrgAdmin && !isOrgEmployee && !isSiteAdmin && !isSiteEmployee;

    const assignedOrgAndSite = useAuth();
    const {
        dealerId: contextDealerId,
        orgId: contextOrgId,
        siteId: contextSiteId,
        siteChangeCallback,
    } = useOrgSiteContext("home");

    const dealerId = useMemo(
        () => contextDealerId || assignedOrgAndSite.dealerId,
        [assignedOrgAndSite.dealerId, contextDealerId]
    );
    const orgId = useMemo(
        () => contextOrgId || assignedOrgAndSite.orgId,
        [assignedOrgAndSite.orgId, contextOrgId]
    );
    const siteId = useMemo(
        () => contextSiteId || assignedOrgAndSite.siteId,
        [contextSiteId, assignedOrgAndSite.siteId]
    );

    const { sites, loading } = useFullSitesLoader({ dealerId, siteId, orgId });
    const site = sites.length === 1 ? sites[0] : undefined;

    useForceRerender(2000);
    const { maintenanceModeStatus } = useContext(SignalRContext);
    const siteMaintenanceModeStatus = site ? maintenanceModeStatus.get(site.id) : undefined;
    const isSiteInMaintenanceMode = siteMaintenanceModeStatus !== undefined
        ? siteMaintenanceModeStatus.isEnabled
        : Boolean(site?.isInMaintenanceMode);

    const { issueCount, issuesByType, load: loadIssues, loading: issuesLoading } = useSiteIssues(site);
    const commissioningSummary = useSiteCommissioningSummary(showHardwareSummary ? site : undefined);

    useEffect(() => {
        if (!site) {
            return;
        }
        hubConnection.on("activeIssuesCountChanged", (changedSiteId: number) => {
            if (changedSiteId !== site.id) return;
            loadIssues();
        });

        return () => {
            hubConnection.off("activeIssuesCountChanged");
        };
    }, [site, loadIssues]);

    const classes = useStyles();

    return (
        <>
            <PageHeader
                helpVideoNum={HelpVideo.Login}
                isLoading={loading}
            >
                Home Page
            </PageHeader>

            <ServerStatus />

            <OrgSelect
                dealerSelectIsEnabled={isGlobalAdmin}
                orgSelectIsEnabled={isGlobalAdmin || isDealerAdmin || isDealerEmployee}
                selectedDealerId={dealerId || null}
                selectedOrgId={orgId || null}
                selectedSiteId={siteId || null}
                onChange={(val: {
                    dealerId?: number | null;
                    orgId?: number | null;
                    siteId?: number | null;
                }) => siteChangeCallback(val.dealerId, val.orgId, val.siteId)}
                bypassSiteContext={deassocciateSiteFromContext}
            />

            <div className={classes.container}>
                <SitesInMaintenanceModeWidget
                    dealerId={dealerId}
                    orgId={orgId}
                    siteId={siteId}
                />

                {sites.length > 1 && (
                    <SitesStats sites={sites} dealerId={dealerId} />
                )}

                {site && (
                    <>
                        <SiteInfo
                            site={site}
                            dealerId={dealerId}
                            commissioningSummary={commissioningSummary}
                            showHardwareSummary={showHardwareSummary}
                        />
                        <div className={classes.widgetsRow}>
                            <SiteIssuesSummary issueCount={issueCount} loading={issuesLoading} />
                            {showCommissioningSiteLink && site.balenaCloudDeviceId && (
                                <OpenCommissioningSiteWidget
                                    url={`https://${site.balenaCloudDeviceId}.${DEVICE_MANAGEMENT_LOGIN_HOST_PATH}`}
                                    enabled={isSiteInMaintenanceMode}
                                />
                            )}
                            {showSiteConfigLink && (
                                <OpenSiteConfigurationWidget
                                    url={navLinkUrl("config", dealerId, orgId, site.id)}
                                />
                            )}
                        </div>
                        <SiteIssuesTable issuesByType={issuesByType} />
                    </>
                )}

                <ReportsLink
                    dealerId={dealerId}
                    orgId={orgId}
                    siteId={siteId}
                />
            </div>
        </>
    );
};